import React, { useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@<>"',;]+@[^\s@<>"',;]+\.[A-Za-z]{2,}$/;
const newId = () => window.crypto?.randomUUID?.() || `inq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const validate = ({ name, email, message }) => {
  const e = {};
  if (!name.trim()) e.name = 'Please enter your name.';
  if (!EMAIL_RE.test(email.trim())) e.email = 'Please enter a valid email address.';
  if (message.trim().length < 2) e.message = 'Please enter a message.';
  return e;
};

const errorText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return 'We couldn’t send your message. Please check your connection and try again, or call or email us directly.';
};

// Contact Us general inquiry form (approved markup); saves to the Inquiries inbox before confirming.
export const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');
  const [status, setStatus] = useState('');
  const submissionId = useRef(newId());
  const startedAt = useRef(Date.now());

  const update = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErrors((x) => ({ ...x, [k]: undefined })); };

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) { setState('error'); setStatus('Please check the highlighted fields.'); return; }
    setState('sending');
    setStatus('Sending your message…');
    try {
      await axios.post(`${API_URL}/api/inquiries`, {
        name: form.name.trim(), email: form.email.trim(), message: form.message.trim(), website: form.website,
        client_submission_id: submissionId.current, elapsed_ms: Date.now() - startedAt.current,
      });
      setState('sent');
      setStatus('Thank you. Your message has been received and our team will reply by email.');
      setForm({ name: '', email: '', message: '', website: '' });
      submissionId.current = newId();
      startedAt.current = Date.now();
    } catch (err) {
      const fields = err?.response?.data?.detail?.fields;
      if (fields) setErrors(fields);
      setState('error');
      setStatus(errorText(err));
    }
  };

  const fieldErr = (k) => errors[k] && <span className="gp-field-error" id={`contact-${k}-error`} data-testid={`contact-${k}-error`}>{errors[k]}</span>;
  const aria = (k) => ({ 'aria-invalid': errors[k] ? 'true' : undefined, 'aria-describedby': errors[k] ? `contact-${k}-error` : undefined });

  return (
    <form id="contact-form" onSubmit={submit} noValidate data-testid="contact-form">
      <div className="field-pair">
        <label htmlFor="contact-name">Name<input id="contact-name" name="name" autoComplete="name" required maxLength={120} placeholder="Your name" value={form.name} onChange={update('name')} {...aria('name')} data-testid="contact-name-input" />{fieldErr('name')}</label>
        <label htmlFor="contact-email">Email<input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={254} placeholder="you@example.com" value={form.email} onChange={update('email')} {...aria('email')} data-testid="contact-email-input" />{fieldErr('email')}</label>
      </div>
      <label htmlFor="contact-message">Message<textarea id="contact-message" name="message" rows={5} required maxLength={5000} placeholder="Tell us how we can help." value={form.message} onChange={update('message')} {...aria('message')} data-testid="contact-message-input" />{fieldErr('message')}</label>
      <div className="gp-hp" aria-hidden="true">
        <label htmlFor="contact-website">Website<input id="contact-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={update('website')} /></label>
      </div>
      <div className="form-bottom">
        <p className="preview-note" aria-hidden="true" />
        <button type="submit" disabled={state === 'sending'} aria-busy={state === 'sending'} data-testid="contact-submit-button">
          {state === 'sending' ? 'SENDING…' : 'SEND MESSAGE'} <span aria-hidden="true">↗</span>
        </button>
      </div>
      <div role="status" aria-live="polite">
        {status && <p id="contact-status" data-state={state} data-testid="contact-status">{status}</p>}
      </div>
    </form>
  );
};
